/**
 * Ledger vocabulary — the merchant's filter words, mapped ONCE onto the receipt classes (MAR-110).
 *
 * ═══════════════════════════════════════════════════════════════════════════════════════
 * THE VOCABULARY TRAP (docs/work/app-ia-v5.md §3). The v5 Ledger's filter words collide with
 * the canonical decision vocabulary: v5's "wait" ("Waiting") is canonical disposition
 * `deferred`, and v5's "skip" ("She chose not to") is canonical judgment `wait`. Reading either
 * word through the other vocabulary silently swaps two populations. So the Ledger never speaks
 * the canonical words at its boundary, and never speaks its own words below it: this module is
 * the one translation, and it translates from `receipt.class` — the shared classifier's answer
 * (`@marva/shared/decision-receipt`) — never from `action` / `status` / `judgment` directly.
 * The filter words are deliberately NOT `wait` / `skip` / `engage`, so a word cannot be
 * mistaken for the canonical value it used to collide with.
 * ═══════════════════════════════════════════════════════════════════════════════════════
 *
 * ── What is deliberately absent ─────────────────────────────────────────────────────────
 *
 * NO `learned` filter ("She learned"). v5 draws one, but no producer writes a "learned"
 * record — there is nothing to filter. It is owned by MAR-58; add it here when a producer
 * exists, never as a filter that always answers empty (an empty filter reads as "she learned
 * nothing", which is a claim).
 *
 * Four classes belong to NO filter and appear only under "all" (`LEDGER_ALL_ONLY_CLASSES`):
 * `decision_failed` and `execution_failed` (something broke), `legacy_ambiguous` (a pre-D2
 * skip we refuse to guess about) and `help_withdrawn` (cancelled/expired before sending).
 * None of them may ever map to `chose_not`: A FAILURE IS NEVER RESTRAINT, and neither is a
 * row we cannot read. The exhaustiveness test in `__tests__/ledger-vocabulary.test.js` fails
 * the moment the classifier gains a class this module has not placed.
 *
 * ── Dependency-free, on purpose ─────────────────────────────────────────────────────────
 * Requires nothing. The web app bundles it (see `packages/web-app/src/types/marva-shared.d.ts`)
 * and the activity-feed Lambda loads it; a vocabulary that builds a client at load costs every
 * consumer a client.
 *
 * @module ledger-vocabulary
 */

'use strict';

/** "All" is not a filter word — it is the absence of one. Accepted at the boundary as a no-op. */
const LEDGER_ALL = 'all';

/** The merchant-facing filter words, in the order a surface should offer them. */
const LEDGER_FILTERS = Object.freeze([
  'acted',
  'asked',
  'chose_not',
  'blocked',
  'waiting',
  'held',
  'you_changed'
]);

/** Filter word → the receipt classes it selects. Disjoint by construction (and by test). */
const LEDGER_FILTER_CLASSES = Object.freeze({
  // Sent, or handed off and on its way.
  acted: Object.freeze(['help_executed', 'help_in_flight']),
  // Marva judged the merchant is needed. (Schema-only today — the prompt never emits it.)
  asked: Object.freeze(['escalate']),
  // Canonical judgment `wait`: Marva chose silence. The restraint arm, and ONLY it.
  chose_not: Object.freeze(['wait']),
  // A help a limit withheld (governance, authority). A help held for the merchant's APPROVAL
  // is also `help_blocked` by class, but files under `asked` — see `filterForReceipt`.
  blocked: Object.freeze(['help_blocked']),
  // Canonical disposition `deferred` — v5's "wait", which is NOT canonical `wait`.
  waiting: Object.freeze(['help_deferred']),
  // Shadow mode: reasoned, never eligible to send.
  held: Object.freeze(['help_shadowed']),
  // The MERCHANT's own applied change (MAR-175): an Ask proposal they confirmed. Not a decision
  // class — see LEDGER_NON_DECISION_CLASSES.
  you_changed: Object.freeze(['merchant_action'])
});

/**
 * The line classes that are NOT the decision classifier's (`@marva/shared/decision-receipt`).
 * `merchant_action` is an applied merchant-action receipt (`merchant-action-receipts`, MAR-175):
 * the merchant's change, not Marva's judgment, so the classifier never emits it and the
 * activity feed files it directly. Explicit, so the exhaustiveness test can tell "a class the
 * classifier lacks by design" from "a class nobody can emit".
 */
const LEDGER_NON_DECISION_CLASSES = Object.freeze(['merchant_action']);

/** The label a surface shows for each filter word (the v6 prototype's exact copy). */
const LEDGER_FILTER_LABELS = Object.freeze({
  acted: 'She acted',
  asked: 'She asked you',
  chose_not: 'She chose not to',
  blocked: 'A limit stopped her',
  waiting: 'Waiting',
  held: 'Held — not sent',
  you_changed: 'You changed'
});

/**
 * The chip a Ledger ROW carries, per receipt class (the v6 prototype's copy). Only the classes
 * listed here get one; every other class (`help_executed`, `help_in_flight`, the failures,
 * `legacy_ambiguous`, `help_withdrawn`) renders with no chip — answered by `chipForClass` as
 * null, never by a default. Keyed by CLASS, not by filter word: `acted` has no chip, and the
 * failures that sit under no filter must not borrow one.
 */
const LEDGER_CLASS_CHIPS = Object.freeze({
  escalate: 'Escalated to you',
  wait: 'She chose not to',
  help_blocked: 'A limit stopped her',
  help_deferred: 'Waiting',
  help_shadowed: 'Held — not sent',
  merchant_action: 'You changed'
});

/**
 * Classes that appear under "all" and under no filter. Explicit, so the exhaustiveness test
 * can tell "deliberately unfiltered" from "forgotten".
 */
const LEDGER_ALL_ONLY_CLASSES = Object.freeze([
  'decision_failed',
  'execution_failed',
  'legacy_ambiguous',
  'help_withdrawn'
]);

/**
 * The channels a decision row can carry — `DecisionSchema.channel` (schemas.js), restated here
 * because schemas.js loads zod and this module must stay dependency-free. A parity test pins
 * the two together.
 */
const LEDGER_CHANNELS = Object.freeze([
  'email', 'sms', 'chat', 'popup', 'contact_capture', 'reaction_prompt'
]);

/**
 * The triggers whose decisions the Ledger rolls up into ONE campaign line (MAR-246): the ones
 * that reach many people for one reason on one day — new-season picks, a restock, a price
 * drop, the browse follow-up sweep. Every other trigger is one person's moment and stays one
 * line — EXCEPT the two short-cluster rollups below (`LEDGER_ROLLUP_KINDS`, MAR-387). The
 * audience of each is still derived per person (a fact about them), never a list; the rollup is
 * a READING of many judgments, not a campaign Marva ran.
 */
const LEDGER_CAMPAIGN_TRIGGERS = Object.freeze([
  'inspiration',
  'back_in_stock',
  'price_drop',
  'browse_timeout'
]);

/**
 * ── Short-cluster rollups (MAR-387, owner decision 2026-10-25) ──────────────────────────
 * This DELIBERATELY overrides "per-person triggers stay one line" above, for exactly two kinds
 * of line, and only when two or more of them fall in one short time cluster:
 *
 *   anonymous_chat  chat lines from shoppers with no customer id whose title is still one of the
 *                   generic fallbacks ("A customer — asked a question", "Replied to a customer in
 *                   chat") — eight of those in five minutes say nothing eight lines can say
 *                   better. A named, or differently-titled, chat is NEVER folded.
 *   greeting_wait   `proactive_trigger` decisions she judged `wait` — "Didn't greet 3 visitors".
 *
 * A rolled-up line's title is COMPOSED by the Ledger (`rollupLedgerTitle` in ledger-line), never
 * written by her, and the line says so (`titleSource: 'composed'`). It opens to its members, each
 * opening its own decision. A failure is never folded (it has no filter word).
 *
 * A cluster: lines of one kind, one generic title and one filter word, each within
 * LEDGER_ROLLUP_GAP_MS of the cluster's oldest member, the whole cluster within
 * LEDGER_ROLLUP_SPAN_MS.
 */
const LEDGER_ROLLUP_KINDS = Object.freeze(['anonymous_chat', 'greeting_wait']);
/** The trigger of the on-site greeting decision (the gateway's proactive greeting). */
const LEDGER_GREETING_TRIGGER = 'proactive_trigger';
const LEDGER_ROLLUP_GAP_MS = 5 * 60 * 1000;
const LEDGER_ROLLUP_SPAN_MS = 30 * 60 * 1000;

/**
 * "Worth a look" (MAR-388, owner decision 2026-10-25): up to three decisions across the window,
 * tier by tier in this order, newest first within a tier. A decision sits in its HIGHEST tier
 * only. Nothing qualifies → nothing pinned. `reason` is the line the block shows beside it.
 */
const LEDGER_WORTH_TIERS = Object.freeze([
  Object.freeze({ tier: 'failed', reason: 'Something broke — she couldn’t finish this one.' }),
  Object.freeze({ tier: 'asked', reason: 'She’s waiting on your call.' }),
  Object.freeze({ tier: 'blocked', reason: 'A limit stopped her.' }),
  Object.freeze({ tier: 'chose_not', reason: 'She chose not to.' }),
  Object.freeze({ tier: 'claimed', reason: 'An order she can claim.' })
]);
/** How many picks the block shows at most. */
const LEDGER_WORTH_MAX = 3;
/** The outcomes that are an order (outcome-vocabulary `CONVERSION_OUTCOMES`, restated: leaf module; a parity test pins them). */
const LEDGER_CLAIMED_OUTCOMES = Object.freeze(['conversion', 'conversion_partial', 'refunded']);

/**
 * The "Worth a look" tier of a decision, or null. Reads the RECEIPT (as the Ledger files it) and
 * the row's `outcome` — never action/status. A failure is `decision_failed`/`execution_failed`;
 * "asked you" is the `asked` filter (an escalation, or a help held for the merchant's approval).
 *
 * @param {{class?: string, pendingApproval?: boolean, unrecognisedStatus?: boolean}|null} receipt
 * @param {string|null|undefined} outcome - the decision row's `outcome`
 * @returns {string|null}
 */
function worthTierOf(receipt, outcome) {
  const cls = receipt && typeof receipt === 'object' ? receipt.class : null;
  if (cls === 'decision_failed' || cls === 'execution_failed') return 'failed';
  const filter = filterForReceipt(receipt);
  if (filter === 'asked') return 'asked';
  if (filter === 'blocked') return 'blocked';
  if (filter === 'chose_not') return 'chose_not';
  if (typeof outcome === 'string' && LEDGER_CLAIMED_OUTCOMES.includes(outcome)) return 'claimed';
  return null;
}

/** The block's reason line for a tier, or null. */
function worthReasonOf(tier) {
  const found = LEDGER_WORTH_TIERS.find((t) => t.tier === tier);
  return found ? found.reason : null;
}

/**
 * How a campaign line's chip counts its members, per receipt class, in the order it says them.
 * Chips MAY state outcomes (the line's title never does). An approval hold is "waiting on
 * you", not "held". A failure is never grouped, so it has no label here.
 */
const LEDGER_CAMPAIGN_OUTCOMES = Object.freeze([
  ['help_executed', 'sent'],
  ['help_in_flight', 'sending'],
  ['approval', 'waiting on you'],
  ['escalate', 'asked you'],
  ['help_deferred', 'waiting'],
  ['help_blocked', 'held'],
  ['help_shadowed', 'held in shadow'],
  ['wait', 'chose not to']
]);

const CLASS_TO_FILTER = Object.freeze(
  Object.entries(LEDGER_FILTER_CLASSES).reduce((acc, [filter, classes]) => {
    for (const cls of classes) acc[cls] = filter;
    return acc;
  }, {})
);

/** True for a filter word (not `all`). */
function isLedgerFilter(word) {
  return typeof word === 'string' && Object.prototype.hasOwnProperty.call(LEDGER_FILTER_CLASSES, word);
}

/**
 * The receipt classes a filter word selects, or null for an unknown word. `all` (or no word)
 * selects every class and is answered with null by `classesForFilter` — callers test
 * `isLedgerFilter` first.
 */
function classesForFilter(word) {
  return isLedgerFilter(word) ? LEDGER_FILTER_CLASSES[word] : null;
}

/**
 * The filter word a receipt class belongs to, or null when it belongs only to "all".
 * Never guesses: an unknown class is null, never `chose_not`. CLASS-ONLY — to file an actual
 * line, use `filterForReceipt`, which also reads `pendingApproval` / `unrecognisedStatus`.
 */
function filterForClass(cls) {
  return (typeof cls === 'string' && Object.prototype.hasOwnProperty.call(CLASS_TO_FILTER, cls))
    ? CLASS_TO_FILTER[cls]
    : null;
}

/** The row chip for a receipt class, or null (no chip). */
function chipForClass(cls) {
  return (typeof cls === 'string' && Object.prototype.hasOwnProperty.call(LEDGER_CLASS_CHIPS, cls))
    ? LEDGER_CLASS_CHIPS[cls]
    : null;
}

/**
 * The filter word for a RECEIPT (the flat `toReceipt` projection the activity feed puts on an
 * entry). THIS is what a surface and the ledger route file a line by — the class alone is not
 * enough, in two places:
 *
 *   - A help held for the merchant's approval (`help_blocked` with `pendingApproval: true`,
 *     status `escalated`) is Marva ASKING the merchant, not a limit stopping her. It files
 *     under `asked`, beside the `escalate` judgment, never under `blocked`.
 *   - A `help_in_flight` with `unrecognisedStatus: true` carries a status that is not a send
 *     state; the classifier already refuses to call it a contact, so it is not "She acted"
 *     either. It sits under "all" only.
 *
 * Every other receipt files by its class (`filterForClass`).
 *
 * @param {{class?: string, pendingApproval?: boolean, unrecognisedStatus?: boolean}|null} receipt
 * @returns {string|null}
 */
function filterForReceipt(receipt) {
  if (!receipt || typeof receipt !== 'object') return null;
  if (receipt.class === 'help_blocked' && receipt.pendingApproval === true) return 'asked';
  if (receipt.class === 'help_in_flight' && receipt.unrecognisedStatus === true) return null;
  return filterForClass(receipt.class);
}

/** The row chip for a receipt — `chipForClass`, with the approval hold read as `asked`. */
function chipForReceipt(receipt) {
  if (!receipt || typeof receipt !== 'object') return null;
  if (receipt.class === 'help_blocked' && receipt.pendingApproval === true) {
    return LEDGER_CLASS_CHIPS.escalate;
  }
  return chipForClass(receipt.class);
}

/**
 * The chip for a campaign line: its members counted by outcome — "9 held · 5 sent". Reads each
 * member's RECEIPT, never action/status. Members with no countable class are left out of the
 * chip (they are still in the line's member count). Null when nothing is countable.
 *
 * @param {Array<{class?: string, pendingApproval?: boolean}|null>} receipts
 * @returns {string|null}
 */
function campaignOutcomeChip(receipts) {
  const counts = new Map();
  for (const receipt of receipts || []) {
    if (!receipt || typeof receipt !== 'object') continue;
    const key = receipt.class === 'help_blocked' && receipt.pendingApproval === true ? 'approval' : receipt.class;
    counts.set(key, (counts.get(key) || 0) + 1);
  }
  const parts = LEDGER_CAMPAIGN_OUTCOMES
    .filter(([key]) => counts.get(key) > 0)
    .map(([key, label]) => `${counts.get(key)} ${label}`);
  return parts.length ? parts.join(' · ') : null;
}

module.exports = {
  LEDGER_ALL,
  LEDGER_ROLLUP_KINDS,
  LEDGER_GREETING_TRIGGER,
  LEDGER_ROLLUP_GAP_MS,
  LEDGER_ROLLUP_SPAN_MS,
  LEDGER_WORTH_TIERS,
  LEDGER_WORTH_MAX,
  LEDGER_CLAIMED_OUTCOMES,
  worthTierOf,
  worthReasonOf,
  LEDGER_CAMPAIGN_TRIGGERS,
  campaignOutcomeChip,
  LEDGER_CLASS_CHIPS,
  LEDGER_FILTERS,
  LEDGER_FILTER_CLASSES,
  LEDGER_FILTER_LABELS,
  LEDGER_ALL_ONLY_CLASSES,
  LEDGER_NON_DECISION_CLASSES,
  LEDGER_CHANNELS,
  isLedgerFilter,
  classesForFilter,
  filterForClass,
  chipForClass,
  filterForReceipt,
  chipForReceipt
};
